import { Link } from '@inertiajs/react'

const FEATURES = [
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <line x1="3" y1="9" x2="21" y2="9" />
        <line x1="9" y1="21" x2="9" y2="9" />
      </svg>
    ),
    title: "Always know exactly what you're owed",
    description:
      'Precise amortization schedules for standard, interest-only, and balloon loans. Every payment auto-splits into principal and interest — down to the penny.',
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
    title: 'Catch problems before they cost you',
    description:
      "LTV creeping above 80%? One borrower at 60% of your capital? No collateral documented? LendSolo flags it while you're setting up the deal — not after.",
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z" />
        <path d="M5 19l1 3 1-3 3-1-3-1-1-3-1 3-3 1 3 1z" />
      </svg>
    ),
    title: 'AI-Powered Insights',
    description:
      "Generate a professional deal memo in seconds — the kind that takes 30 minutes to write by hand. Get plain-English risk analysis that connects the dots between LTV, concentration, and collateral quality. Not just badges and numbers — actual insight into what your loan data means.",
    badge: 'Pro',
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
      </svg>
    ),
    title: 'Never miss a payment again',
    description:
      'Record payments in two clicks. Automated email reminders to borrowers before due dates, late notices when they miss, and receipts when they pay.',
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <rect x="2" y="3" width="20" height="14" rx="2" />
        <line x1="8" y1="21" x2="16" y2="21" />
        <line x1="12" y1="17" x2="12" y2="21" />
      </svg>
    ),
    title: 'Run the numbers before you wire the money',
    description:
      'ROI projections, loan comparisons, and interest-only calculators. Free, no account required — evaluate your next deal right now.',
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
      </svg>
    ),
    title: 'Tax season in 30 seconds, not 3 hours',
    description:
      'Year-end interest income summary, expense reports for Schedule C, and accountant-ready exports in PDF, CSV, and QBO. Hit download, send to your CPA, done.',
    badge: 'Pro plan',
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 00-3-3.87" />
        <path d="M16 3.13a4 4 0 010 7.75" />
      </svg>
    ),
    title: 'Deal Room',
    description:
      'Post deals. Collect commitments. Lock the round. All in one place. Syndicate loans to your investors without the email chaos.',
    badge: 'Fund',
    link: '/deal-room',
  },
]

export default function FeaturesGrid() {
  return (
    <section id="features" className="py-20 sm:py-24" style={{ backgroundColor: '#f8f7f4' }}>
      <div className="max-w-[1140px] mx-auto px-8">
        <p className="text-[11px] font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-3">
          What changes
        </p>
        <h2 className="font-display text-3xl sm:text-[40px] leading-[1.15] text-[#0f1a2e] tracking-tight mb-4">
          Everything a private lender actually needs
        </h2>
        <p className="text-[17px] leading-relaxed text-[#5a6578] max-w-[600px] mb-6">
          No bloat. No features built for banks. Just the things that make the difference between running your lending business and just surviving it.
        </p>
        <div className="flex items-center gap-2 mb-12 text-sm text-[#5a6578] bg-[#ede9fe]/40 border border-[#ede9fe] rounded-lg px-4 py-2.5 max-w-[600px]">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6d28d9" strokeWidth="2" strokeLinecap="round" className="min-w-4">
            <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z" />
          </svg>
          <span>
            <span className="font-medium text-[#6d28d9]">AI-powered:</span>{' '}
            LendSolo uses AI to write deal memos and risk analyses that would take you hours to research.
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f) => {
            const inner = (
              <>
                <div className="w-11 h-11 min-w-[44px] rounded-[10px] bg-[#e8f5ee] text-[#1a7a4c] flex items-center justify-center">
                  {f.icon}
                </div>
                <div>
                  <h3 className="text-base font-semibold text-[#0f1a2e] mb-1.5">{f.title}</h3>
                  <p className="text-sm leading-relaxed text-[#5a6578]">{f.description}</p>
                  {f.badge && (
                    <span className="inline-block mt-2 font-mono text-[10px] tracking-wide px-2 py-0.5 rounded bg-[#e8f0fc] text-[#1e5fa6]">
                      {f.badge}
                    </span>
                  )}
                </div>
              </>
            )
            const className = "bg-white border border-[#e2e0d8] rounded-xl p-8 flex gap-4 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200"

            return f.link ? (
              <Link key={f.title} href={f.link} className={className}>
                {inner}
              </Link>
            ) : (
              <div key={f.title} className={className}>
                {inner}
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
